// Planilla de micrófono: PDF con la grilla de partidos de una jornada, agrupada
// por cancha y por horario. No lleva casillero de resultado: es la lista que se
// lee por micrófono para llamar a los equipos.
//
// Los partidos que se dieron por walk-over van tachados (solo las dos celdas de
// ese partido: en la misma fila puede haber otra cancha jugándose normalmente).
//
// Solo entran los partidos que ya tienen cancha Y horario: uno sin programar no
// tiene lugar en la grilla, así que queda afuera (ver `estaProgramado`).
//
// Se arma una planilla por turno (ver `turnosDeJornada`). Cada planilla entra en
// una hoja A4: las canchas van de a dos, compartiendo la columna de horario, y en
// la hoja entran dos de esos grupos (mitad superior y mitad inferior). Con más de
// 4 canchas se agregan hojas. La tipografía y el alto de las filas se calculan
// antes de dibujar (ver `elegirLayout`) para que cada grupo entre en su mitad.
//
// jspdf/jspdf-autotable se cargan con import() dinámico para que no entren en el
// bundle principal de la PWA: solo se bajan cuando se toca el botón.

import { fechaCalendario } from "./fechas";
import { agruparPorCancha, enMinutos, estaProgramado } from "./horarios";
import { esWO } from "./resultado";

// jsPDF mide las fuentes en puntos y la hoja en milímetros.
const MM_POR_PUNTO = 0.3528;
const INTERLINEADO = 1.15;

const CANCHAS_POR_GRUPO = 2;
const GRUPOS_POR_HOJA = 2;

// Clave de orden para los partidos sin hora, que van al final de la grilla.
const SIN_HORA = Number.POSITIVE_INFINITY;

const limpiarNombreArchivo = (texto) => texto.replace(/[\\/:*?"<>|]/g, "-").replace(/\s+/g, " ").trim();

// Las canchas se reparten de a CANCHAS_POR_GRUPO (grupos que comparten la columna
// de horario) y los grupos de a GRUPOS_POR_HOJA.
function repartirEnHojas(grupos) {
  const pares = [];
  for (let i = 0; i < grupos.length; i += CANCHAS_POR_GRUPO)
    pares.push(grupos.slice(i, i + CANCHAS_POR_GRUPO));

  const hojas = [];
  for (let i = 0; i < pares.length; i += GRUPOS_POR_HOJA) hojas.push(pares.slice(i, i + GRUPOS_POR_HOJA));
  return hojas;
}

// Filas de un grupo de canchas: una por horario, con los partidos de cada cancha
// uno al lado del otro. Si en un horario alguna cancha no tiene partido, sus
// casilleros quedan vacíos.
//
// Devuelve { filas, celdasWO }: `celdasWO[i]` es el Set de columnas de la fila i
// que corresponden a un partido dado por WO (las que después se tachan).
function filasDelGrupo(grupo, nombreDeEquipo) {
  const porHorario = grupo.map((cancha) => {
    const mapa = new Map();
    cancha.partidos.forEach((m) => {
      const minutos = enMinutos(m.hora);
      const clave = minutos == null ? SIN_HORA : minutos;
      if (!mapa.has(clave)) mapa.set(clave, []);
      mapa.get(clave).push(m);
    });
    return mapa;
  });

  const horarios = [...new Set(porHorario.flatMap((mapa) => [...mapa.keys()]))].sort((a, b) => a - b);

  const filas = [];
  const celdasWO = [];
  horarios.forEach((clave) => {
    const enHorario = porHorario.map((mapa) => mapa.get(clave) || []);
    const cantidad = Math.max(...enHorario.map((partidos) => partidos.length));
    const hora = (enHorario.flat()[0] || {}).hora || "";

    for (let i = 0; i < cantidad; i++) {
      const fila = [i === 0 ? hora : ""];
      const wo = new Set();
      enHorario.forEach((partidos) => {
        const m = partidos[i];
        if (m && esWO(m)) wo.add(fila.length).add(fila.length + 1);
        fila.push(m ? nombreDeEquipo(m.homeTeamId) : "", m ? nombreDeEquipo(m.awayTeamId) : "");
      });
      filas.push(fila);
      celdasWO.push(wo);
    }
  });

  return { filas, celdasWO };
}

// torneoNombre / torneoAno: encabezan cada planilla ("TORNEO APERTURA 2026").
// numero: el "Fecha N" de la jornada. dia: la fecha calendario (ISO).
// sedeNombre: el nombre de la sede (ya resuelto).
// turnos: [{ id, nombre, partidos }] — una planilla por cada uno, en hoja aparte.
export async function generarPlanillaMicrofono({
  torneoNombre,
  torneoAno,
  numero,
  dia,
  sedeNombre,
  turnos = [],
  teams = [],
}) {
  // Los partidos sin cancha u horario no se listan; un turno que se queda sin
  // partidos no genera hoja. De acá sale todo lo demás: subtítulo, hojas y nombre
  // del archivo.
  const turnosEnJuego = turnos
    .map((turno) => ({ ...turno, partidos: (turno.partidos || []).filter(estaProgramado) }))
    .filter((turno) => turno.partidos.length > 0);
  if (turnosEnJuego.length === 0) return;

  const nombreDeEquipo = (teamId) => {
    const t = teams.find((x) => x.id === teamId);
    return t ? t.name : "—";
  };

  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const anchoPagina = doc.internal.pageSize.getWidth();
  const altoPagina = doc.internal.pageSize.getHeight();
  const margen = 12;
  const anchoUtil = anchoPagina - margen * 2;
  const anchoHora = 15;
  const anchoEquipo = (anchoUtil - anchoHora) / (CANCHAS_POR_GRUPO * 2);
  // Cada grupo de canchas ocupa media hoja; el respiro separa la mitad de arriba
  // de la de abajo.
  const altoMitad = (altoPagina - margen * 2) / GRUPOS_POR_HOJA;
  const respiro = 6;
  const altoDisponible = altoMitad - respiro;

  const nombreDelTorneo = [torneoNombre || "Torneo", torneoAno].filter(Boolean).join(" ");
  const titulo = nombreDelTorneo.toUpperCase();
  // El turno solo se aclara cuando el PDF trae los dos: con uno solo, la planilla
  // lleva el encabezado justo (torneo, fecha y sede).
  const subtituloDe = (turno) =>
    [
      numero ? "Fecha " + numero : "",
      fechaCalendario({ date: dia }),
      sedeNombre,
      turnosEnJuego.length > 1 ? turno.nombre : "",
    ]
      .filter(Boolean)
      .join(" · ");
  const haySubtitulo = !!subtituloDe(turnosEnJuego[0]);

  // Cada turno arma sus propias hojas; el layout se elige una sola vez para todo
  // el PDF, así todas las planillas se ven iguales.
  const planillas = turnosEnJuego.map((turno) => ({
    turno,
    hojas: repartirEnHojas(agruparPorCancha(turno.partidos)),
  }));
  const todosLosGrupos = planillas.flatMap((p) => p.hojas.flat());

  const nombresEnJuego = todosLosGrupos
    .flatMap((grupo) => grupo.flatMap((cancha) => cancha.partidos))
    .flatMap((m) => [nombreDeEquipo(m.homeTeamId), nombreDeEquipo(m.awayTeamId)]);

  const anchoDeTexto = (texto, fontSize, estilo = "normal") => {
    doc.setFont("helvetica", estilo);
    doc.setFontSize(fontSize);
    return doc.getTextWidth(texto);
  };

  // Ancho del nombre de equipo más largo, para elegir un cuerpo de letra en el que
  // ningún nombre se parta en dos renglones (si se parte, la fila crece y el grupo
  // se sale de su mitad de hoja).
  const anchoNombreMasLargo = (fontSize) =>
    nombresEnJuego.reduce((max, n) => Math.max(max, anchoDeTexto(n, fontSize)), 0);

  // Medidas concretas de un layout candidato: `escala` achica el alto de las filas
  // y la tipografía hasta que el grupo más cargado entre en media hoja.
  const medirLayout = (escala) => {
    const padding = Math.max(0.6, 1.4 * escala);
    const anchoTextoEquipo = anchoEquipo - padding * 2;

    let fontBody = Math.min(9, Math.max(5, 9 * escala));
    while (fontBody > 5 && anchoNombreMasLargo(fontBody) > anchoTextoEquipo) fontBody -= 0.25;

    const renglones = Math.max(1, Math.ceil(anchoNombreMasLargo(fontBody) / anchoTextoEquipo));
    const altoFila = Math.max(4.5 * escala, renglones * fontBody * MM_POR_PUNTO * INTERLINEADO + padding * 2);

    const fontTitulo = Math.max(8, Math.min(12, 12 * escala));
    const fontSubtitulo = Math.max(6.5, Math.min(9.5, 9.5 * escala));
    const fontCancha = Math.max(7, Math.min(10, 10 * escala));
    const altoTitulo = fontTitulo * MM_POR_PUNTO + 1.5;
    const altoSubtitulo = haySubtitulo ? fontSubtitulo * MM_POR_PUNTO + 2 : 0;
    const altoCancha = fontCancha * MM_POR_PUNTO + 1.5;

    const altoDelGrupo = (grupo) =>
      altoTitulo + altoSubtitulo + altoCancha + filasDelGrupo(grupo, nombreDeEquipo).filas.length * altoFila;
    const altoNecesario = Math.max(0, ...todosLosGrupos.map(altoDelGrupo));

    return {
      padding,
      fontBody,
      fontTitulo,
      fontSubtitulo,
      fontCancha,
      altoFila,
      altoTitulo,
      altoSubtitulo,
      altoCancha,
      entra: altoNecesario <= altoDisponible,
    };
  };

  const elegirLayout = () => {
    for (let escala = 1; escala >= 0.4; escala -= 0.02) {
      const layout = medirLayout(escala);
      if (layout.entra) return layout;
    }
    // Ni al mínimo entra (una cancha con muchísimos partidos): se usa lo más chico
    // y el grupo se pasa de su mitad.
    return medirLayout(0.4);
  };

  const layout = elegirLayout();

  const dibujarGrupo = (grupo, topeDelGrupo, subtitulo) => {
    let y = topeDelGrupo;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(layout.fontTitulo);
    doc.setTextColor(0, 0, 0);
    doc.text(titulo, anchoPagina / 2, y + layout.fontTitulo * MM_POR_PUNTO, { align: "center" });
    y += layout.altoTitulo;

    if (subtitulo) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(layout.fontSubtitulo);
      doc.text(subtitulo, anchoPagina / 2, y + layout.fontSubtitulo * MM_POR_PUNTO, { align: "center" });
      y += layout.altoSubtitulo;
    }

    // El título de cada cancha va sobre sus dos columnas de equipos.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(layout.fontCancha);
    grupo.forEach((cancha, indice) => {
      const x = margen + anchoHora + indice * anchoEquipo * 2 + layout.padding;
      doc.text(cancha.titulo, x, y + layout.fontCancha * MM_POR_PUNTO);
    });
    y += layout.altoCancha;

    const columnStyles = { 0: { cellWidth: anchoHora, halign: "center", fontStyle: "bold" } };
    for (let i = 1; i <= grupo.length * 2; i++) columnStyles[i] = { cellWidth: anchoEquipo, halign: "center" };

    const { filas, celdasWO } = filasDelGrupo(grupo, nombreDeEquipo);

    autoTable(doc, {
      startY: y,
      margin: { top: margen, bottom: 0, left: margen, right: margen },
      body: filas,
      theme: "grid",
      // Los grupos de una sola cancha (la última, cuando son impares) ocupan la
      // mitad del ancho en vez de estirarse.
      tableWidth: "wrap",
      styles: {
        font: "helvetica",
        fontSize: layout.fontBody,
        cellPadding: layout.padding,
        minCellHeight: layout.altoFila,
        valign: "middle",
        lineColor: [90, 90, 90],
        lineWidth: 0.2,
        textColor: [0, 0, 0],
      },
      columnStyles,
      // Los partidos dados por WO van tachados: autotable no sabe tachar, así que
      // la línea se dibuja a mano sobre las dos celdas de ese partido.
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        const wo = celdasWO[data.row.index];
        if (!wo || !wo.has(data.column.index)) return;
        const medio = data.cell.y + data.cell.height / 2;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.4);
        doc.line(data.cell.x, medio, data.cell.x + data.cell.width, medio);
      },
    });
  };

  let primeraHoja = true;
  planillas.forEach(({ turno, hojas }) => {
    const subtitulo = subtituloDe(turno);
    hojas.forEach((grupos) => {
      if (!primeraHoja) doc.addPage();
      primeraHoja = false;
      grupos.forEach((grupo, indice) => dibujarGrupo(grupo, margen + indice * altoMitad, subtitulo));
    });
  });

  const nombreDelTurno = turnosEnJuego.length === 1 ? turnosEnJuego[0].nombre : "Ambos Turnos";
  const nombreArchivo = limpiarNombreArchivo(
    ["Planilla Microfono", nombreDelTorneo, numero ? "Fecha " + numero : "", nombreDelTurno, dia || ""]
      .filter(Boolean)
      .join(" - ")
  );

  doc.save(nombreArchivo + ".pdf");
}
